import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/CustomTable'
import Loading from '@/components/Loading'
import { useDebounce } from '@/hooks/useDebounce'
import { useTableUrlState } from '@/hooks/useTableUrlState'
import { useContainerSelectQuery } from '@/hooks/useContainers'
import { useInventoryLevelsQuery } from '@/hooks/useInventoryLevels'
import type { InventoryLevelRow } from '@/types/backend'
import { ImportInitialStockDialog } from '@/components/inventory/ImportInitialStockDialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

export default function InventoryPage() {
  const navigate = useNavigate()
  const [importOpen, setImportOpen] = useState(false)

  const {
    state: { search, pageIndex, pageSize },
    setSearch,
    setPageIndex,
    setPageSize,
  } = useTableUrlState('inventory', { search: '', pageIndex: 0, pageSize: 20 })

  const debounced = useDebounce(search, 400)

  const { data: containers } = useContainerSelectQuery(undefined, true)
  const [containerId, setContainerId] = useState<string>('ALL')

  const {
    data: list,
    status,
    isFetching,
  } = useInventoryLevelsQuery(
    {
      search: debounced || undefined,
      containerId: containerId === 'ALL' ? undefined : containerId,
      page: pageIndex + 1,
      pageSize,
    },
    true
  )

  const total = list?.total ?? 0
  const rows = list?.data ?? []
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  )

  const columns = useMemo<ColumnDef<InventoryLevelRow>[]>(
    () => [
      {
        accessorKey: 'itemName',
        header: 'Produit',
        cell: ({ row }) => (
          <button
            className='font-medium underline-offset-2 hover:underline'
            onClick={() => navigate(`/items/${row.original.itemId}`)}
          >
            {row.original.itemName}
          </button>
        ),
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        cell: ({ row }) => row.original.sku || '—',
      },
      {
        accessorKey: 'containerName',
        header: 'Conteneur',
        cell: ({ row }) => row.original.containerName || '—',
      },
      {
        accessorKey: 'qty',
        header: 'Qté',
        cell: ({ row }) => (
          <span className='tabular-nums'>{row.original.qty ?? 0}</span>
        ),
      },
    ],
    [navigate]
  )

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between gap-3'>
        <h3 className='text-2xl font-semibold'>Inventaire</h3>
        <Button variant='secondary' onClick={() => setImportOpen(true)}>
          Importer stock initial (CSV)
        </Button>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder='Rechercher (nom / sku)…'
          className='w-full sm:w-[260px]'
        />

        <Select value={containerId} onValueChange={setContainerId}>
          <SelectTrigger className='w-[240px]'>
            <SelectValue placeholder='Tous les conteneurs' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='ALL'>Tous les conteneurs</SelectItem>
            {(containers ?? []).map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.isDefault ? `★ ${c.name}` : c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {status === 'pending' ? (
        <Loading />
      ) : (
        <>
          <div className='text-sm text-muted-foreground'>
            Total: <b>{total}</b> {isFetching ? '— actualisation…' : null}
          </div>

          <DataTable<InventoryLevelRow, unknown>
            columns={columns}
            data={rows}
            paginationServer
            total={total}
            pageCount={pageCount}
            searchValue={search}
            onSearchChange={setSearch}
            currentPageIndex={pageIndex}
            currentPageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
          />
        </>
      )}

      <ImportInitialStockDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        defaultContainerId={containerId === 'ALL' ? undefined : containerId}
      />
    </div>
  )
}
